import React, { useEffect, useState } from "react";
import { IconCaretLeft, IconCaretRight, IconRefresh } from "@tabler/icons";
import { Group, Select, ActionIcon, Button, Box, Text } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import moment from "moment";
import { useTranslation } from "react-i18next";
import RemoteSelect2 from "./remoteSelect2";
import { IconX } from "@tabler/icons";
import ReactJson from "react-json-view";
import { useForceUpdate } from "@mantine/hooks";

export const OrderFilter = ({
  numPeriods = 8,
  /** When forward + Month mode: include this many months before current (q = -1, -2, …). Default month stays q = 0. */
  pastPeriods = 0,
  handlePeriodChange = () => {},
  handleRefresh = () => {},
  refreshLoading = false,
  handleUserChange = () => {},
  position = "right",
  forward = false,

  by = "Q",
  iconButton = false,
  showPeriodOptionsSelector = false,
  withStaffFilter = false,
  withProductCategoryFilter = false,
  onProductCategoryChange = () => {},
  specificUser = null,
  onDateTypeChange = () => {},
  hideDateTypeSelector = false,
  ...props
}) => {
  const [q, setQ] = useState(0);
  const [periodFilterOptions, setPeriodFilterOptions] = useState([]);
  const [periodOption, setPeriodOption] = useState(by || "Q");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [userSelected, setUserSelected] = useState(specificUser);
  const [productCategory, setProductCategory] = useState(null);
  const [dateType, setDateType] = useState("signedDate");
  const { t } = useTranslation();
  const forceUpdate = useForceUpdate();
  const DATA_REPORT_DATE_TYPE = [
    { value: "signedDate", label: "Sign Date" },
    { value: "policyCommenceDate", label: "Policy Commence Date" },
    { value: "createdAt", label: "Create Date" },
  ];

  const setPeriodOptions = () => {
    // console.log("setPeriodOptions", periodOption, by);
    if (periodOption == "C") return;
    const filters = [];
    const usePastMonths =
      forward && pastPeriods > 0 && periodOption === "M";

    if (usePastMonths) {
      for (let q = -pastPeriods; q < numPeriods; q++) {
        const d = moment().add(q, periodOption);
        filters.push({ label: d.format(`YYYY-MM`), value: q });
      }
    } else {
      for (let i = 0; i < numPeriods; i++) {
        const d = forward
          ? moment().add(i, periodOption)
          : moment().subtract(i, periodOption);

        if (periodOption === "Q")
          filters.push({ label: d.format(`YYYY-[Q]Q`), value: i });
        if (periodOption === "M")
          filters.push({ label: d.format(`YYYY-MM`), value: i });
        if (periodOption === "Y")
          filters.push({ label: d.format(`YYYY`), value: i });
      }
    }
    setPeriodFilterOptions(filters);
    hanldeQChange(0);
  };

  const qMin =
    forward && pastPeriods > 0 && periodOption === "M" ? -pastPeriods : 0;
  const qMax = numPeriods - 1;

  /** With forward=false, q increases toward older periods (subtract from today). Prev/Next must swap vs forward=true so labels match time direction (same as Gi renewal). */
  const stepPeriodOlder = () =>
    forward
      ? hanldeQChange(q - 1 < qMin ? qMin : q - 1)
      : hanldeQChange(q + 1 > qMax ? qMax : q + 1);
  const stepPeriodNewer = () =>
    forward
      ? hanldeQChange(q + 1 > qMax ? qMax : q + 1)
      : hanldeQChange(q - 1 < qMin ? qMin : q - 1);

  useEffect(() => {
    setPeriodOptions();
  }, [periodOption, by, numPeriods, pastPeriods, forward]);

  const periodOptions = [
    { label: "Month", value: "M" },
    { label: "Quarter", value: "Q" },
    { label: "Year", value: "Y" },
    { label: "Custom", value: "C" },
  ];

  const hanldeQChange = (q) => {
    // Calculate the target month/period based on q
    let targetDate = forward
      ? moment().add(q, periodOption)
      : moment().subtract(q, periodOption);

    // For the period, we want the full period range
    // e.g., if q=0 and periodOption="M", we want the current full month
    let start = targetDate.clone().startOf(periodOption).format("YYYY-MM-DD");
    let end = targetDate.clone().endOf(periodOption).format("YYYY-MM-DD");

    setStartDate(start);
    setEndDate(end);
    setQ(q);
  };

  useEffect(() => {
    handlePeriodChange({ startDate, endDate, dateType });
    forceUpdate();
  }, [startDate, endDate, dateType]);

  useEffect(() => {
    if (specificUser) {
      setUserSelected(specificUser);
      return;
    }
    handleUserChange(userSelected);
    forceUpdate();
  }, [userSelected]);

  useEffect(() => {
    onProductCategoryChange(productCategory);
    forceUpdate();
  }, [productCategory]);

  const clearFilters = () => {
    if (!specificUser) setUserSelected(null);
    if (withProductCategoryFilter) setProductCategory(null);
  };

  return (
    <>
      {/* <ReactJson
        src={{ specificUser }}
        style={{ width: "100%", background: "white" }}
      /> */}
      {/* start : {startDate} <br />
      end : {endDate} */}
      <Group position={position} spacing="xs" align="center" noWrap>
        {withProductCategoryFilter && (
          <Box sx={{ width: "280px" }}>
            <RemoteSelect2
              size={"xs"}
              apiEntity="productCategory"
              labelField="name"
              labelRender={(data) =>
                `${data.name ?? ""}${data.cname ? ` ${data.cname}` : ""}`
              }
              searchFields={["name", "code", "cname"]}
              valueField="_id"
              value={productCategory}
              onDataChange={setProductCategory}
              onChange={setProductCategory}
              width="100%"
              clearable={true}
              pageSize={200}
              placeholder={t("orderFilter.bizLine", "Biz Line")}
              sort={{ by: "name", order: "asc" }}
              select="_id name cname code"
            />
          </Box>
        )}
        {periodOption != "C" && (
          <Box sx={{ width: "180px" }}>
            <Select
              size={"xs"}
              data={periodFilterOptions}
              value={q}
              onChange={hanldeQChange}
              defaultValue={0}
              style={{ width: "100%" }}
            />
          </Box>
        )}
        {!hideDateTypeSelector && (
          <Box sx={{ width: "200px" }}>
            <Select
              size={"xs"}
              data={DATA_REPORT_DATE_TYPE}
              value={dateType}
              onChange={(value) => {
                setDateType(value);
                onDateTypeChange(value);
              }}
              style={{ width: "100%" }}
            />
          </Box>
        )}
        {periodOption == "C" && (
          <Group spacing="xs" noWrap>
            <Box sx={{ width: "180px" }}>
              <DatePicker
                withinPortal
                size={"xs"}
                value={
                  startDate ? moment(startDate).startOf("day").toDate() : null
                }
                onChange={setStartDate}
                style={{ width: "100%" }}
                allowFreeInput
              />
            </Box>
            <Box sx={{ width: "180px" }}>
              <DatePicker
                withinPortal
                size={"xs"}
                value={endDate ? moment(endDate).endOf("day").toDate() : null}
                onChange={setEndDate}
                style={{ width: "100%" }}
                allowFreeInput
              />
            </Box>
          </Group>
        )}

        {showPeriodOptionsSelector && (
          <Box sx={{ width: "100px" }}>
            <Select
              size={"xs"}
              data={periodOptions}
              value={periodOption}
              onChange={(value) => {
                console.log("PeriodOptionChange", value);
                setPeriodOption(value);
              }}
              style={{ width: "100%" }}
            />
          </Box>
        )}
        {withStaffFilter && !specificUser && (
          <Box sx={{ width: "350px" }}>
            <RemoteSelect2
              size={"xs"}
              apiEntity="user"
              labelField="name"
              labelRender={(data) =>
                `[${data.staffId}] ${data.name ?? ""} ${data.cname ?? ""} ${
                  data.aliasName ? `(${data.aliasName})` : ""
                }`
              }
              searchFields={["name", "staffId", "aliasName"]}
              valueField="_id"
              value={userSelected}
              onDataChange={(user) => {
                // console.log("onDataChange", user);
                setUserSelected(user);
              }}
              onChange={(user) => {
                // console.log("onChange", user);
                setUserSelected(user);
              }}
              // onKeyDown={handleKeyDown}
              width="100%"
              clearable={true}
              pageSize={2000}
              placeholder="Select TR / Referer"
              sort={{ by: "staffId", order: "asc" }}
              select="_id staffId name cname aliasName email"
            />
          </Box>
        )}

        {
          //Clear User
          !specificUser && (
            <Button
              size="xs"
              variant="light"
              color="gray"
              leftIcon={<IconX size={14} />}
              onClick={clearFilters}
            >
              Clear
            </Button>
          )
        }

        {handleRefresh && (
          <Button
            size="xs"
            variant="light"
            color="blue"
            leftIcon={<IconRefresh size={14} />}
            onClick={handleRefresh}
            loading={refreshLoading}
            disabled={refreshLoading}
          >
            Refresh
          </Button>
        )}

        {periodOption != "C" && (
          <Group spacing="xs" noWrap>
            <Button
              size="xs"
              variant="light"
              color="gray"
              leftIcon={<IconCaretLeft size={14} />}
              onClick={stepPeriodOlder}
            >
              Prev
            </Button>

            <Button
              size="xs"
              variant="light"
              color="gray"
              rightIcon={<IconCaretRight size={14} />}
              onClick={stepPeriodNewer}
            >
              Next
            </Button>
          </Group>
        )}
      </Group>
    </>
  );
};
